import {
  IUser
} from "@/types";
import { Table } from '@/components';
import { MoreOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, Space, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';
import { FaEdit, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const USER_STATUS = { ACTIVE: 1, DEACTIVATED: 2 } as const;

interface UserTableViewProps {
  users: IUser[];
  loading?: boolean;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  onEditPersonalDetails: (user: IUser) => void;
  onEditRole: (user: IUser) => void;
  onEditRegionTeam: (user: IUser) => void;
  onDelete: (user: IUser) => void;
  onChangeStatus: (user: IUser) => void;
  canEditUsers?: boolean;
  canDeactivateUsers?: boolean;
  canManageRoles?: boolean;
  canChangeRegion?: boolean;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Helper function to get default role from user's roles array
const getDefaultRole = (user: IUser): string => {
  if (!user.dogma_AppRole_SystemUser_SystemUser || user.dogma_AppRole_SystemUser_SystemUser.length === 0) {
    return '-';
  }

  // Find the default role (where dogma_defaultsecurityrole is true)
  const defaultRole = user.dogma_AppRole_SystemUser_SystemUser.find(
    (role) => role.dogma_defaultsecurityrole === true
  );

  return defaultRole?.dogma_name || user.dogma_AppRole_SystemUser_SystemUser[0]?.dogma_name || '-';
};

export const UserTableView: React.FC<UserTableViewProps> = ({
  users,
  loading = false,
  onEditPersonalDetails,
  onEditRole,
  onEditRegionTeam,
  onDelete,
  onChangeStatus,
  canEditUsers = true,
  canDeactivateUsers = true,
  canManageRoles = true,
  canChangeRegion = true,
}) => {
  const [localPage, setLocalPage] = useState(1);
  const localPageSize = 20;
  const paginatedUsers = users.slice((localPage - 1) * localPageSize, localPage * localPageSize);

  const columns: ColumnsType<IUser> = [
    {
      title: 'Name',
      key: 'name',
      width: 250,
      ellipsis: true,
      render: (_, record) => {
        const firstName = record.firstname || '';
        const lastName = record.lastname || '';
        const initials = (firstName[0] || '') + (lastName[0] || '');

        return (
          <Space>
            <Avatar style={{ backgroundColor: '#8b1538' }}>
              {initials}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, color: '#1a1a1a' }}>
                {firstName} {lastName}
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Email',
      key: 'email',
      dataIndex: 'internalemailaddress',
      width: 250,
      ellipsis: true,
      render: (email, record) => (
        <Tooltip title={email || record.domainname}>
          <div style={{ color: '#1a1a1a' }}>{email || record.domainname}</div>
        </Tooltip>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 180,
      render: (_, record) => (
        <Tag color="blue">
          {getDefaultRole(record)}
        </Tag>
      ),
    },
    {
      title: 'Region',
      key: 'region',
      dataIndex: 'dogma_region@OData.Community.Display.V1.FormattedValue',
      width: 160,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'Team',
      key: 'team',
      dataIndex: 'dogma_team@OData.Community.Display.V1.FormattedValue',
      width: 180,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      key: 'dogma_userstatus',
      width: 130,
      render: (_, record) => {
        const status = record.dogma_userstatus;
        if (status === USER_STATUS.ACTIVE) {
          return <Tag color="green">Active</Tag>;
        }
        if (status === USER_STATUS.DEACTIVATED) {
          return <Tag color="red">Deactivated</Tag>;
        }
        return <Tag>-</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          canEditUsers && {
            key: 'edit-personal',
            icon: <FaEdit />,
            label: 'Edit Personal Details',
            onClick: () => onEditPersonalDetails(record),
          },
          canManageRoles && {
            key: 'edit-role',
            icon: <FaEdit />,
            label: 'Edit Role & Permissions',
            onClick: () => onEditRole(record),
          },
          canChangeRegion && {
            key: 'edit-region',
            icon: <FaEdit />,
            label: 'Edit Region & Team',
            onClick: () => onEditRegionTeam(record),
          },
          canDeactivateUsers && {
            key: 'change-status',
            icon: record.dogma_userstatus === USER_STATUS.ACTIVE ? <FaToggleOff /> : <FaToggleOn />,
            label: record.dogma_userstatus === USER_STATUS.ACTIVE ? 'Deactivate User' : 'Activate User',
            onClick: () => onChangeStatus(record),
          },
        ].filter(Boolean);

        // If no actions available, don't show dropdown
        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <MoreOutlined style={{ cursor: 'pointer', fontSize: 20 }} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={paginatedUsers}
      isLoading={loading}
      rowKey="systemuserid"
      current={localPage}
      pageSize={localPageSize}
      total={users.length}
      handleChange={(page) => setLocalPage(page)}
      scroll={{ x: 1600 }}
    />
  );
};

export default UserTableView;
